/**
 * Composant Header avec navigation organisée par rôles
 * Utilise des sous-menus pour réduire l'encombrement
 */

import { Link } from 'react-router-dom';
import BlockableLink from './BlockableLink.jsx';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useConnectivity } from '../../contexts/ConnectivityContext.jsx';
import { useOfflineSync } from '../../contexts/OfflineSyncContext.jsx';
import * as supportService from '../../services/support.service.js';
import * as documentationService from '../../services/documentation.service.js';
import NavDropdown from './NavDropdown.jsx';
import UserAvatar from './UserAvatar.jsx';
import { useGovernorPermission } from '../../hooks/useGovernorPermission.js';
import NotificationCenter from '../NotificationCenter/NotificationCenter.jsx';
import InstallAppButton from '../pwa/InstallAppButton.jsx';
import ThemeToggle from '../common/ThemeToggle.jsx';
import { getUserTypeLabel, getUserTypeShortLabel } from '../../utils/userTypeLabels.js';
import { getFeatures } from '../../services/meta.service.js';
import { getRoleHomePath } from '../../config/navigation.js';
import './Header.css';
import './UserAvatar.css';

function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const canValidateUsers = useGovernorPermission('validate_users');
  const [newSupportCount, setNewSupportCount] = useState(0);
  const [escaladeCount, setEscaladeCount] = useState(0);
  const { isReachable } = useConnectivity();
  const { pendingCount } = useOfflineSync();

  // F-35 : des actions hors ligne pas encore envoyées restent sur l'appareil,
  // rattachées à ce compte — on prévient avant de se déconnecter.
  const handleLogout = () => {
    if (pendingCount > 0) {
      const ok = window.confirm(
        `${pendingCount} action(s) pas encore synchronisée(s) — elles seront envoyées à votre prochaine connexion sur cet appareil. Se déconnecter ?`,
      );
      if (!ok) return;
    }
    logout();
  };
  const [canReadDocs, setCanReadDocs] = useState(false);
  // Registre témoin : quand le coupe-circuit est ouvert, la nav sympathisant
  // pointe vers les cas vérifiés (la Contribution legacy est morte — 410).
  const [fondsEnabled, setFondsEnabled] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    getFeatures().then((f) => setFondsEnabled(f.fonds_enabled === true)).catch(() => {});
  }, [isAuthenticated]);

  const loadNewSupportCount = async () => {
    try {
      const response = await supportService.getSupportBadges();
      if (response.success) {
        setNewSupportCount(response.data.total_attention || 0);
        setEscaladeCount(response.data.escalade || 0);
      }
    } catch (err) {
      console.error('Erreur loadNewSupportCount:', err);
      setNewSupportCount(0);
      setEscaladeCount(0);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.type === 'admin') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadNewSupportCount();
      const interval = setInterval(loadNewSupportCount, 60000);
      return () => clearInterval(interval);
    }
    setNewSupportCount(0);
    setEscaladeCount(0);
  }, [isAuthenticated, user]);

  // Documentation access is controlled by an admin-managed allow-list.
  // Re-evaluated on user change so role updates and admin grants take effect
  // on the next session refresh.
  useEffect(() => {
    let cancelled = false;
    if (!(isAuthenticated && user)) {
      // Reset doc-access flag when the auth state turns falsy. Safe one-shot
      // sync of derived state; not a render loop.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCanReadDocs(false);
      return () => {};
    }
    (async () => {
      try {
        const res = await documentationService.getAccess();
        if (!cancelled) setCanReadDocs(!!res?.data?.canRead);
      } catch {
        if (!cancelled) setCanReadDocs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const relaisType = user?.relais_type || user?.relais?.type || null;

  const typeLabel = getUserTypeLabel(user?.type, relaisType);
  const typeShortLabel = getUserTypeShortLabel(user?.type, relaisType);

  // Logo + « Tableau de bord » → role home (never public `/` while logged in —
  // that flashed the welcome page then bounced back with a layout reflow).
  const dashboardPath = isAuthenticated ? getRoleHomePath(user) : '/';

  const adminMenuItems = [
    {
      sectionLabel: 'Gestion',
      items: [
        { to: '/admin/users', label: 'Utilisateurs' },
        { to: '/admin/elections', label: 'Élections' },
        { to: '/admin/carousel', label: 'Carousel' },
      ],
    },
    // Section « Finance » retirée du menu (décision fondateur 19/07/2026) :
    // les pages de configuration/monitoring des fournisseurs de paiement sont
    // des reliques de l'ère « garde des fonds », contraires à la charte de
    // l'argent (l'app est témoin, jamais banque). Routes conservées jusqu'au
    // nettoyage custody post-v1 (TODOS.md) — accès direct par URL seulement.
    {
      sectionLabel: 'Données',
      items: [
        { to: '/admin/users/map', label: 'Carte GPS' },
        { to: '/admin/localites', label: 'Localités' },
        { to: '/admin/journal', label: 'Journal' },
        { to: '/admin/logs', label: 'Logs' },
        { to: '/admin/reports', label: 'Rapports' },
        { to: '/admin/parcours-unlock-audit', label: 'Audit déverrouillage parcours' },
      ],
    },
    {
      sectionLabel: 'Système',
      items: [
        { to: '/admin/configuration', label: 'Configuration' },
        { to: '/admin/cron', label: 'Tâches planifiées' },
        { to: '/admin/sms-configuration', label: 'OTP Configuration' },
        { to: '/admin/notification-settings', label: 'Notifications' },
        { to: '/admin/documentation-access', label: 'Documentation (accès)' },
        {
          to: '/admin/support-cases',
          label:
            escaladeCount > 0
              ? `Cas support / Escalades (${escaladeCount})`
              : newSupportCount > 0
                ? `Cas support (${newSupportCount})`
                : 'Cas support / Escalades',
        },
      ],
    },
  ];

  // Revue fondateur 02/08 : Relais « Bénéficiaires » + « Actions » retirés du header —
  // déjà couverts par les action-cards du dashboard (+ bottom/side nav).

  // Revue fondateur 02/08 : « Mon activité » + « Statistiques » retirés du header
  // Parrain GE — déjà couverts par les action-cards du dashboard (+ bottom nav Classement).

  // Revue 20/07 : « Contribuer » (flux custody legacy) retiré — ses endpoints
  // répondent 410 sans condition ; le financement passe par les cas vérifiés.
  const parrainSympaContributionItems = [
    { to: '/parrain-sympa/cas-verifies', label: 'Financer un cas vérifié' },
    { to: '/parrain-sympa/payment-methods', label: 'Mes Paiements' },
  ];

  const aboutMenuItems = [
    { to: '/about/mission', label: 'Notre mission' },
    { to: '/about/support', label: 'Support' },
    { to: '/about/version', label: 'Version' },
  ];

  const connectivityBadge = (
    <span
      className={`header-connectivity ${isReachable ? 'header-connectivity-online' : 'header-connectivity-offline'}`}
      role="status"
      aria-live="polite"
      aria-label={isReachable ? 'Application en ligne' : 'Application hors ligne'}
      title={isReachable ? 'Connexion disponible' : 'Pas de connexion réseau'}
    >
      <span className="header-connectivity-dot" aria-hidden />
      {isReachable ? 'En ligne' : 'Hors ligne'}
    </span>
  );

  return (
    <header className="header">
      <div className="header-container">
        <BlockableLink to={dashboardPath} className="logo">
          {/* Deux lockups (v2, 20/07) : or sur transparent en Mode Nuit — le
              vert du lockup jour est illisible sur la surface nuit. Le CSS
              (Header.css) n'en affiche qu'un selon [data-theme]. */}
          <img
            src="/assets/images/logo/logo-main.png"
            alt="Grand-Électeur"
            className="logo-image logo-image-day"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.closest('.logo')?.querySelector('.logo-text');
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <img
            src="/assets/images/logo/logo-main-night.png"
            alt="Grand-Électeur"
            className="logo-image logo-image-night"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.closest('.logo')?.querySelector('.logo-text');
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <h1 className="logo-text">Grand-Électeur</h1>
        </BlockableLink>

        <nav className="nav" aria-label="Navigation principale">
          {isAuthenticated ? (
            <>
              <BlockableLink to={dashboardPath} className="nav-link">
                Tableau de bord
              </BlockableLink>
              <NotificationCenter />
              <InstallAppButton />

              {user?.type === 'parrain_sympa' && (
                <>
                  {fondsEnabled ? (
                    <BlockableLink to="/parrain-sympa/cas-verifies" className="nav-link">Cas vérifiés</BlockableLink>
                  ) : (
                    <NavDropdown label="Contribution" items={parrainSympaContributionItems} LinkComponent={BlockableLink} />
                  )}
                  <BlockableLink to="/parrain-sympa/rankings" className="nav-link">Classement</BlockableLink>
                </>
              )}

              {(user?.type === 'gouverneur' || user?.type === 'admin') && canValidateUsers && (
                <BlockableLink to="/gouverneur/validation" className="nav-link">Validation</BlockableLink>
              )}

              {user?.type === 'admin' && (
                <NavDropdown label="Administration" items={adminMenuItems} LinkComponent={BlockableLink} />
              )}

              {canReadDocs && (
                <BlockableLink to="/documentation" className="nav-link">
                  Documentation
                </BlockableLink>
              )}

              <NavDropdown label="À propos" items={aboutMenuItems} LinkComponent={BlockableLink} />

              <div className="user-menu header-right-group">
                <ThemeToggle />
                <BlockableLink to="/profile" className="header-profile-link" title="Mon profil">
                  <UserAvatar user={user} size="sm" showRing />
                  <span className="header-profile-name">
                    {user?.first_name || user?.last_name
                      ? [user.first_name, user.last_name].filter(Boolean).join(' ')
                      : user?.telephone || 'Profil'}
                  </span>
                </BlockableLink>
                <span className="user-info" title={typeLabel}>
                  ({typeShortLabel})
                </span>
                <button
                  onClick={handleLogout}
                  className="btn-logout"
                  title="Déconnexion"
                  aria-label="Déconnexion"
                >
                  Déconnexion
                </button>
              </div>
              {connectivityBadge}
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link header-right-group">Connexion</Link>
              <Link to="/register" className="nav-link">Inscription</Link>
              <ThemeToggle />
              {/* Installable aussi avant connexion : le meilleur moment pour un
                  relais/bénéficiaire d'installer, avant d'aller sur le terrain. */}
              <InstallAppButton />
              {connectivityBadge}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;
